/**
 * WordPress dependencies
 */
import apiFetch from '@safe-wordpress/api-fetch';
import { CheckboxControl } from '@safe-wordpress/components';
import { useState } from '@safe-wordpress/element';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import './style.scss';
import { ConnectionMode, GA4Property, DEFAULT_ATTRS } from './config';

import { Connection } from './connection';
import { openConnectionAuthorizer } from './utils';

import { useAttributes } from '../hooks';
import type { FieldSettingProps } from '../types';

export const GoogleAnalyticsDataSetting = ( {
	name: settingName,
	disabled,
}: FieldSettingProps ): JSX.Element => {
	const [ attributes, setAttributes ] = useAttributes(
		settingName,
		DEFAULT_ATTRS
	);
	const { _enabled, propertyId, propertyName } = attributes;

	const [ mode, setMode ] = useState< ConnectionMode >(
		!! propertyId && !! propertyName ? 'ga4-property-id' : 'init'
	);

	const [ properties, setProperties ] = useState<
		ReadonlyArray< GA4Property >
	>( [] );

	const connectAccount = () =>
		openConnectionAuthorizer( {
			onOpen: () => {
				setMode( 'awaiting-authorization' );
			},
			onClose: () => {
				setMode( 'property-retrieval' );
				void apiFetch< GA4Property[] >( {
					path: '/nab/v1/recommendation-engine/ga4-properties',
				} )
					.then( ( foundProperties ) => {
						setProperties( foundProperties );
						const firstProp = foundProperties[ 0 ];
						if ( firstProp ) {
							setAttributes( {
								_enabled: true,
								propertyId: firstProp.id,
								propertyName: firstProp.name,
							} );
						}
						setMode( 'property-selection' );
					} )
					.catch( ( error ) => {
						// eslint-disable-next-line no-console
						console.log( error );
						setAttributes( {
							_enabled: false,
							propertyId: '',
							propertyName: '',
						} );
						setMode( 'init' );
					} );
			},
		} );

	const selectProperty = ( p: GA4Property ) =>
		setAttributes( {
			_enabled: true,
			propertyId: p.id,
			propertyName: p.name,
		} );

	const reset = () => {
		setAttributes( {
			_enabled: false,
		} );
		setMode( 'init' );
	};

	return (
		<div>
			<CheckboxControl
				label={ sprintf(
					/* translators: %s: Nelio AI. */
					_x(
						'Include Google Analytics data to improve %s’s suggestions',
						'command',
						'nelio-ab-testing'
					),
					'Nelio AI'
				) }
				disabled={ disabled }
				checked={ _enabled }
				onChange={ ( checked ) => {
					setAttributes( { _enabled: checked } );
					if ( ! checked ) {
						reset();
					} else if ( ! properties.length ) {
						if ( ! propertyId ) {
							connectAccount();
						} else {
							setMode( 'ga4-property-id' );
						}
					} else {
						setMode( 'property-selection' );
					}
				} }
			/>
			<Connection
				className="nab-google-analytics-data-setting__connection"
				disabled={ disabled }
				mode={ mode }
				selectedProperty={ { id: propertyId, name: propertyName } }
				properties={ properties }
				onChangeAccount={ connectAccount }
				onSelectProperty={ selectProperty }
				currentProperty={ {
					id: propertyId,
					name: propertyName,
				} }
			/>
			<input
				type="hidden"
				name={ settingName }
				value={ JSON.stringify( attributes ) }
			/>
		</div>
	);
};
